import type { Kind, Meal, Order, OrderStatus, Pass, PassState, Sitting, EventStatus } from './types';

const TZ = 'Asia/Kolkata';

export const inr = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN');

export const KIND_LABEL: Record<Kind, string> = { veg: 'Veg', nonveg: 'Non-veg', common: 'Bhog' };
/** Closed or archived: over, nothing to book, pay or serve. */
export const eventEnded = (status: EventStatus | undefined) => status === 'closed' || status === 'archived';

export const MEAL_LABEL: Record<Meal, string> = { breakfast: 'Breakfast', lunch: 'Lunch', snacks: 'Snacks', dinner: 'Dinner' };

export function mealName(s: Pick<Sitting, 'meal' | 'day_key'> & { label?: string | null }): string {
  if (s.label) return s.label;
  return s.day_key === 'ashtami' && s.meal === 'lunch' ? 'Bhog lunch' : MEAL_LABEL[s.meal];
}

/** How an order is being paid, for lists: "Paid online", "UTR ••••3456", "Not paid yet" (never "UTR not sent"). */
export function payMethodText(o: Pick<Order, 'total' | 'status' | 'pay_mode' | 'paid_amount' | 'online_pending' | 'cash_recorded_at' | 'utr' | 'legacy_utr_last4'>): string {
  if (o.total === 0) return 'Nothing to pay';
  if (o.pay_mode === 'online') return o.paid_amount != null ? 'Paid online' : o.online_pending ? 'Online payment started' : 'Online';
  if (o.pay_mode === 'cash') return o.cash_recorded_at ? 'Cash received' : 'Cash at the help desk';
  if (o.utr || o.legacy_utr_last4) return `UTR ${maskUtr(o.utr, o.legacy_utr_last4)}`;
  return o.status === 'confirmed' ? 'Paid' : 'Not paid yet';
}

/** The event(s) passes are for, for titles: "Durga Pujo 2026", "Diwali Celebration 2026". */
export const eventTitle = (passes: Pick<Pass, 'sitting'>[]) =>
  [...new Set(passes.map((p) => p.sitting.event_name).filter(Boolean))].join(' · ') || 'Food passes';

export function seats(adults: number, children: number): string {
  const p: string[] = [];
  if (adults) p.push(`${adults} adult`);
  if (children) p.push(`${children} child`);
  return p.join(' · ') || '0';
}

/** Flats are a floor (1–20) and a letter: 3B, 20F. "3b", "03B", "3-b" → "3B". */
export function cleanFlat(s: string): string {
  return s.toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/^0+(?=\d)/, '');
}

export const isFlat = (s: string) => /^([1-9]|1[0-9]|20)[A-Z]$/.test(cleanFlat(s));

export function fmtTime(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true })
    .format(new Date(iso)).replace(/\s?(am|pm)$/i, (m) => ' ' + m.trim().toUpperCase());
}

export function fmtDay(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: 'numeric', month: 'short' }).format(new Date(iso));
}

/** "Fri 16 Oct" in IST. */
export function fmtWeekday(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' })
    .format(new Date(iso)).replace(',', '');
}

export function fmtDateTime(iso: string): string {
  return `${fmtDay(iso)}, ${fmtTime(iso)}`;
}

export function windowText(s: Pick<Sitting, 'opens_at' | 'closes_at'>): string {
  return `${fmtTime(s.opens_at)}–${fmtTime(s.closes_at)}`;
}

export function serveDateText(s: Pick<Sitting, 'serve_date'>): string {
  return fmtDay(s.serve_date + 'T12:00:00+05:30');
}

/** Days of an event with the year: "3 Nov 2026", "16–20 Oct 2026", "30 Oct – 2 Nov 2026", "30 Dec 2026 – 2 Jan 2027". */
export function dateRangeText(from: string | null, to: string): string {
  const parts = (d: string) => {
    const p = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric' })
      .formatToParts(new Date(d + 'T12:00:00+05:30'));
    const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
    return { d: get('day'), m: get('month'), y: get('year') };
  };
  const b = parts(to);
  if (!from || from === to) return `${b.d} ${b.m} ${b.y}`;
  const a = parts(from);
  if (a.y !== b.y) return `${a.d} ${a.m} ${a.y} – ${b.d} ${b.m} ${b.y}`;
  if (a.m !== b.m) return `${a.d} ${a.m} – ${b.d} ${b.m} ${b.y}`;
  return `${a.d}–${b.d} ${b.m} ${b.y}`;
}

/** "2026-10-16T08:00" in IST for <input type="datetime-local"> */
export function toIstInput(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 5.5 * 3600 * 1000);
  return d.toISOString().slice(0, 16);
}

/** Inverse of toIstInput. */
export function fromIstInput(value: string): string {
  return new Date(value + ':00+05:30').toISOString();
}

export function maskUtr(utr: string | null, legacy?: string | null): string {
  if (utr) return '••••' + utr.slice(-4);
  if (legacy) return '••••' + legacy + ' (last 4)';
  return 'Not sent';
}

export function utrGroups(utr: string): [string, string, string] {
  return [utr.slice(0, 4), utr.slice(4, 8), utr.slice(8)];
}

export const ORDER_STATUS: Record<OrderStatus, { label: string; tone: '' | 'ok' | 'warn' | 'bad' }> = {
  awaiting_payment: { label: 'Awaiting payment', tone: '' },
  awaiting_check: { label: 'Awaiting check', tone: 'warn' },
  confirmed: { label: 'Confirmed', tone: 'ok' },
  sent_back: { label: 'Sent back', tone: 'bad' },
  spam: { label: 'Not accepted', tone: '' },
  cancelled: { label: 'Cancelled', tone: '' },
};

export const COMMITTEE_STATUS_LABEL: Record<OrderStatus, string> = {
  awaiting_payment: 'Awaiting payment',
  awaiting_check: 'Awaiting check',
  confirmed: 'Confirmed',
  sent_back: 'Sent back',
  spam: 'Spam',
  cancelled: 'Cancelled',
};

export function passChip(p: Pick<Pass, 'state' | 'served' | 'booked'>): { label: string; tone: '' | 'ok' | 'warn' | 'bad' } {
  const m: Record<PassState, { label: string; tone: '' | 'ok' | 'warn' | 'bad' }> = {
    locked: { label: 'Locked', tone: '' },
    ready: { label: 'Ready', tone: 'ok' },
    partly_served: { label: `${p.served} of ${p.booked} served`, tone: 'warn' },
    fully_served: { label: 'Fully served', tone: '' },
    expired: { label: p.served > 0 ? `${p.served} of ${p.booked} served` : 'Not used', tone: '' },
    void: { label: 'Cancelled', tone: '' },
  };
  return m[p.state];
}

export function normalizePhone(input: string): string {
  const d = input.replace(/\D/g, '');
  if (/^91[6-9]\d{9}$/.test(d)) return d.slice(2);
  if (/^0[6-9]\d{9}$/.test(d)) return d.slice(1);
  return d;
}

export const isIndianMobile = (d: string) => /^[6-9]\d{9}$/.test(d);

export function maskPhone(p: string): string {
  return p.length === 10 ? `${p.slice(0, 2)}••• ••${p.slice(-3)}` : p;
}

/**
 * The UTR as typed or pasted: the 12-digit number when the text has exactly one (pasted from the UPI app or a bank SMS:
 * "UPI Ref No. 418322973456"), otherwise its digits. A long digit run inside letters is a transaction ID, not the UTR.
 */
export function readUtr(text: string): { utr: string; txnId?: boolean } {
  const twelve = [...text.matchAll(/(?:^|\D)(\d{12})(?=\D|$)/g)].map((m) => m[1]);
  if (twelve.length === 1) return { utr: twelve[0] };
  const digits = text.replace(/\D/g, '');
  if (/[a-z]/i.test(text) && digits.length > 12) return { utr: '', txnId: true };
  return { utr: digits.slice(0, 12) };
}
